import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  BookOpen, Heart, Share2, ChevronLeft, ChevronRight, 
  Sun, Clock, Bell, BellOff, Navigation, Loader2, RotateCw, Search, ArrowLeft, ScrollText, X, Book, Library
} from 'lucide-react';

/* =========================================
   1. SABİT VERİLER VE KONFİGÜRASYON (DATA)
   ========================================= */

const SURAH_NAMES_TR = [
  "Fâtiha", "Bakara", "Âl-i İmrân", "Nisâ", "Mâide", "En'âm", "A'râf", "Enfâl", "Tevbe", "Yûnus",
  "Hûd", "Yûsuf", "Ra'd", "İbrâhîm", "Hicr", "Nahl", "İsrâ", "Kehf", "Meryem", "Tâhâ",
  "Enbiyâ", "Hac", "Mü'minûn", "Nûr", "Furkân", "Şu'arâ", "Neml", "Kasas", "Ankebût", "Rûm",
  "Lokmân", "Secde", "Ahzâb", "Sebe'", "Fâtır", "Yâsîn", "Sâffât", "Sâd", "Zümer", "Mü'min",
  "Fussilet", "Şûrâ", "Zuhruf", "Duhân", "Câsiye", "Ahkâf", "Muhammed", "Fetih", "Hucurât", "Kâf",
  "Zâriyât", "Tûr", "Necm", "Kamer", "Rahmân", "Vâkıa", "Hadîd", "Mücâdele", "Haşr", "Mümtehine",
  "Saf", "Cuma", "Münâfikûn", "Teğâbûn", "Talâk", "Tahrîm", "Mülk", "Kalem", "Hâkka", "Meâric",
  "Nûh", "Cin", "Müzzemmil", "Müddessir", "Kıyâme", "İnsân", "Mürselât", "Nebe'", "Nâziât", "Abese",
  "Tekvîr", "İnfitâr", "Mutaffifîn", "İnşikâk", "Burûc", "Târık", "A'lâ", "Gâşiye", "Fecr", "Beled",
  "Şems", "Leyl", "Duhâ", "İnşirah", "Tîn", "Alak", "Kadir", "Beyyine", "Zilzâl", "Âdiyât",
  "Kâria", "Tekâsür", "Asr", "Hümeze", "Fîl", "Kureyş", "Mâûn", "Kevser", "Kâfirûn", "Nasr",
  "Tebbet", "İhlâs", "Felak", "Nâs"
];

const dailyContentData = [
  { type: 'daily', id: 'daily-1', hadith: "Kolaylaştırınız, zorlaştırmayınız. Müjdeleyiniz, nefret ettirmeyiniz.", hadithSource: "Buhârî, İlim, 11", cssClass: "card-emerald" },
  { type: 'daily', id: 'daily-2', hadith: "Cennet annelerin ayakları altındadır.", hadithSource: "Nesâî, Cihad, 6", cssClass: "card-indigo" },
  { type: 'daily', id: 'daily-3', hadith: "Müminin durumu ne hoştur! Her hâli kendisi için hayırlıdır.", hadithSource: "Müslim, Zühd, 64", cssClass: "card-amber" },
  { type: 'daily', id: 'daily-4', hadith: "İki nimet vardır ki insanların çoğu onların kıymetini bilmez: Sağlık ve boş vakit.", hadithSource: "Buhârî, Rikak, 1", cssClass: "card-rose" }
];

const hadithBooks = [
  { id: 'buhari', name: "Sahih-i Buhârî", author: "İmam Buhârî", count: "7563 Hadis", cssClass: "book-blue" },
  { id: 'muslim', name: "Sahih-i Müslim", author: "İmam Müslim", count: "3033 Hadis", cssClass: "book-emerald" },
  { id: 'riyaz', name: "Riyâzüs Salihîn", author: "İmam Nevevî", count: "1896 Hadis", cssClass: "book-amber" },
  { id: 'tirmizi', name: "Sünen-i Tirmizî", author: "İmam Tirmizî", count: "3956 Hadis", cssClass: "book-rose" },
  { id: 'ebudavud', name: "Sünen-i Ebû Dâvûd", author: "Ebû Dâvûd", count: "5274 Hadis", cssClass: "book-slate" },
  { id: 'ibnmace', name: "Sünen-i İbn Mâce", author: "İbn Mâce", count: "4341 Hadis", cssClass: "book-indigo" },
];

const baseHadiths = [
  { id: 'h-1', bookId: 'buhari', text: "Ameller niyetlere göredir.", source: "Buhârî, 1", topic: "Niyet" },
  { id: 'h-2', bookId: 'buhari', text: "Sizin en hayırlınız Kur'an'ı öğrenen ve öğretendir.", source: "Buhârî, 21", topic: "İlim" },
  { id: 'h-3', bookId: 'buhari', text: "Kolaylaştırınız, zorlaştırmayınız.", source: "Buhârî, 3", topic: "Kolaylık" },
  { id: 'h-4', bookId: 'buhari', text: "Utanmıyorsan dilediğini yap!", source: "Buhârî, 4", topic: "Haya" },
  { id: 'h-5', bookId: 'buhari', text: "Müslüman, diğer Müslümanların elinden ve dilinden emin olduğu kimsedir.", source: "Buhârî, 5", topic: "Güven" },
  { id: 'h-6', bookId: 'buhari', text: "Biriniz kendisi için istediğini kardeşi için de istemedikçe iman etmiş olmaz.", source: "Buhârî, 6", topic: "Kardeşlik" },
  { id: 'h-7', bookId: 'buhari', text: "Komşusu açken tok yatan bizden değildir.", source: "Buhârî, 7", topic: "Yardımlaşma" },
];

const fullHadithCollection = [
  ...baseHadiths,
  ...baseHadiths.map(h => ({ ...h, id: h.id.replace('buhari', 'muslim'), bookId: 'muslim', source: h.source.replace('Buhârî', 'Müslim') })),
  ...baseHadiths.map(h => ({ ...h, id: h.id.replace('buhari', 'riyaz'), bookId: 'riyaz', source: h.source.replace('Buhârî', 'Riyâzüs Salihîn') })),
  ...baseHadiths.map(h => ({ ...h, id: h.id.replace('buhari', 'tirmizi'), bookId: 'tirmizi', source: h.source.replace('Buhârî', 'Tirmizî') })),
];

/* =========================================
   2. YARDIMCI FONKSİYONLAR (UTILS)
   ========================================= */

const getFormattedDate = () => {
  return new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' });
};

const calculateQiblaAngle = (lat, lng) => {
  const meccaLat = 21.4225;
  const meccaLng = 39.8262;
  const dLng = (meccaLng - lng) * (Math.PI / 180);
  const lat1 = lat * (Math.PI / 180);
  const lat2 = meccaLat * (Math.PI / 180);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  let brng = Math.atan2(y, x);
  brng = brng * (180 / Math.PI);
  return (brng + 360) % 360;
};

const calculateNextPrayerInfo = (timings) => {
  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const prayers = [
    { name: 'İmsak', time: timings.Fajr },
    { name: 'Güneş', time: timings.Sunrise },
    { name: 'Öğle', time: timings.Dhuhr },
    { name: 'İkindi', time: timings.Asr },
    { name: 'Akşam', time: timings.Maghrib },
    { name: 'Yatsı', time: timings.Isha }
  ];
  let next = null;
  let minDiff = Infinity;
  for (let p of prayers) {
    const [h, m] = p.time.split(':').map(Number);
    const pTime = h * 60 + m;
    let diff = pTime - currentTime;
    if (diff < 0) diff += 24 * 60; 
    if (diff < minDiff) { minDiff = diff; next = p; }
  }
  
  const hours = Math.floor(minDiff / 60);
  const minutes = minDiff % 60;
  const seconds = 59 - now.getSeconds(); 
  
  return {
    nextPrayer: next,
    timeLeft: `${hours}sa ${minutes}dk ${seconds}sn`
  };
};

/* =========================================
   3. ALT BİLEŞENLER (SUB-COMPONENTS)
   ========================================= */

const Header = ({ onOpenFavorites, hasFavorites }) => (
  <header className="header">
    <div className="header-left">
       <div className="logo-box">H</div>
       <div className="header-text">
          <h1 className="app-title">Huzur Vakti</h1>
          <p className="app-subtitle">{getFormattedDate()}</p>
       </div>
    </div>
    <button onClick={onOpenFavorites} className="btn-icon">
       <Heart className={`icon-md ${hasFavorites ? 'text-red fill-red' : 'text-gray'}`} />
    </button>
  </header>
);

const BottomNav = ({ activeTab, setActiveTab }) => (
  <nav className="bottom-nav">
    <div className="nav-grid">
      <button onClick={() => setActiveTab('quran')} className={`nav-item ${activeTab === 'quran' ? 'active' : ''}`}>
        <BookOpen className="icon-sm" /><span>Kuran</span>
      </button>
      <button onClick={() => setActiveTab('hadith')} className={`nav-item ${activeTab === 'hadith' ? 'active' : ''}`}>
        <ScrollText className="icon-sm" /><span>Hadis</span>
      </button>
      <div className="fab-wrapper">
         <button onClick={() => setActiveTab('daily')} className={`fab-btn ${activeTab === 'daily' ? 'active' : ''}`}>
           <Sun className={`icon-md ${activeTab === 'daily' ? 'text-white fill-emerald-light' : 'text-emerald-light'}`} />
         </button>
      </div>
      <button onClick={() => setActiveTab('prayers')} className={`nav-item ${activeTab === 'prayers' ? 'active' : ''}`}>
        <Clock className="icon-sm" /><span>Vakitler</span>
      </button>
      <button onClick={() => setActiveTab('qibla')} className={`nav-item ${activeTab === 'qibla' ? 'active' : ''}`}>
        <Navigation className="icon-sm" /><span>Kıble</span>
      </button>
    </div>
  </nav>
);

const FavoritesModal = ({ show, onClose, favorites, toggleFavorite }) => {
  if (!show) return null;
  return (
    <div className="modal-overlay fade-in">
       <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title"><Heart className="icon-sm text-red fill-current"/> Favorilerim</h2>
            <button onClick={onClose} className="btn-icon bg-gray-light"><X className="icon-sm text-gray"/></button>
          </div>
          <div className="modal-body">
             {favorites.length === 0 ? <div className="empty-favs">Henüz favori eklemediniz.</div> : favorites.map((fav, i) => (
                <div key={i} className="card fav-card">
                  <span className={`badge ${fav.type === 'daily' ? 'badge-emerald' : fav.type === 'quran-ayah' ? 'badge-blue' : 'badge-amber'}`}>
                      {fav.type === 'daily' ? 'Günlük' : fav.type === 'quran-ayah' ? `Kuran • ${fav.surahName} ${fav.number}` : `Hadis • ${fav.topic}`}
                  </span>
                  {fav.type === 'daily' && <p className="text-italic mb-2">"{fav.hadith}"</p>}
                  {fav.type === 'quran-ayah' && <p className="text-normal mb-2">{fav.textTr}</p>}
                  {fav.id && fav.id.startsWith('h-') && <p className="text-normal mb-2">"{fav.text}"</p>}
                  {fav.type === 'quran-ayah' && <p className="text-arabic" dir="rtl">{fav.textAr}</p>}
                  <p className="text-small text-muted">{fav.ayahSource || fav.hadithSource || fav.source}</p>
                  <button onClick={() => toggleFavorite(fav)} className="btn-icon btn-fav active"><Heart className="icon-sm fill-current" /></button>
              </div>
             ))}
          </div>
       </div>
    </div>
  );
};

/* --- TAB GÖRÜNÜM BİLEŞENLERİ --- */

const DailyView = ({ favorites, toggleFavorite, showNotification, prayerTimes }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('right');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [prayerInfo, setPrayerInfo] = useState({ nextPrayer: null, timeLeft: '' });
  const cardRef = useRef(null);

  useEffect(() => {
    // HTML2Canvas Load
    const script = document.createElement('script');
    script.src = "https://html2canvas.hertzen.com/dist/html2canvas.min.js";
    script.async = true;
    document.body.appendChild(script);
    
    // Timer
    if(prayerTimes) {
      const timer = setInterval(() => setPrayerInfo(calculateNextPrayerInfo(prayerTimes)), 1000);
      return () => { clearInterval(timer); if(document.body.contains(script)) document.body.removeChild(script); };
    }
  }, [prayerTimes]);

  const onTouchStart = (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
  const onTouchMove = (e) => { setTouchEnd(e.targetTouches[0].clientX); };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    if (distance > minSwipeDistance) { setSlideDirection('right'); setCurrentIndex((prev) => (prev + 1) % dailyContentData.length); }
    if (distance < -minSwipeDistance) { setSlideDirection('left'); setCurrentIndex((prev) => (prev - 1 + dailyContentData.length) % dailyContentData.length); }
  };

  const handleShare = async () => {
    if (!cardRef.current || !window.html2canvas) { showNotification("Hazırlanıyor..."); return; }
    setIsSharing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const canvas = await window.html2canvas(cardRef.current, { useCORS: true, scale: 2, backgroundColor: '#ffffff' });
      canvas.toBlob(async (blob) => {
        if (!blob) { setIsSharing(false); return; }
        const file = new File([blob], "huzur-vakti.png", { type: "image/png" });
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], title: 'Huzur Vakti' }); showNotification("Paylaşıldı!"); } catch (error) {}
        } else {
          const link = document.createElement('a'); link.href = canvas.toDataURL("image/png"); link.download = "huzur-vakti.png"; link.click(); showNotification("İndirildi.");
        }
        setIsSharing(false);
      });
    } catch (err) { setIsSharing(false); showNotification("Hata oluştu."); }
  };

  const currentDaily = dailyContentData[currentIndex];

  return (
    <div className="fade-in">
      <div 
        ref={cardRef} 
        key={currentIndex} 
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} 
        className={`card daily-card ${slideDirection === 'right' ? 'slide-in-right' : 'slide-in-left'}`}
      >
        <div className={`card-header-bg ${currentDaily.cssClass}`}>
          <div className="pattern-overlay"></div>
          <ScrollText className="icon-lg text-white opacity-90" />
          <h2 className="text-white font-serif">Günün Hadisi</h2>
        </div>
        <div className="card-body">
          <div className="hadith-only-content">
            <p className="ayah-text">"{currentDaily.hadith}"</p>
            <p className="source-text">{currentDaily.hadithSource}</p>
          </div>
          <div className="action-row" data-html2canvas-ignore>
            <button onClick={() => toggleFavorite(currentDaily)} className={`btn-circle ${favorites.some(f=>f.id===currentDaily.id) ? 'btn-red' : 'btn-gray'}`}><Heart className={`icon-sm ${favorites.some(f=>f.id===currentDaily.id) ? 'fill-current' : ''}`} /></button>
            <button onClick={handleShare} disabled={isSharing} className="btn-circle btn-gray">{isSharing ? <Loader2 className="icon-sm spin text-emerald" /> : <Share2 className="icon-sm" />}</button>
          </div>
        </div>
      </div>
      
      <div className="dots-container">{dailyContentData.map((_, idx) => (<div key={idx} className={`dot ${idx === currentIndex ? 'dot-active' : ''}`} />))}</div>

      <div className="home-prayer-container fade-in">
         <div className="home-prayer-left">
            <span className="text-muted text-xs font-bold uppercase tracking-wider">Sonraki Vakit</span>
            <div className="flex items-center gap-2 mt-1">
               <span className="text-emerald text-xl font-bold">{prayerInfo.nextPrayer?.name || '--'}</span>
               <span className="text-gray text-sm font-medium">vaktine</span>
            </div>
         </div>
         <div className="home-prayer-right">
            <div className="timer-box">{prayerInfo.timeLeft || '--:--:--'}</div>
         </div>
      </div>
    </div>
  );
};

const QuranView = ({ favorites, toggleFavorite, showNotification }) => {
  const [surahList, setSurahList] = useState([]);
  const [filteredSurahList, setFilteredSurahList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [quranMode, setQuranMode] = useState('list');
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [quranData, setQuranData] = useState([]);
  const [currentAyahIndex, setCurrentAyahIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right');

  // Swipe logic for reader
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const onTouchStart = (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
  const onTouchMove = (e) => { setTouchEnd(e.targetTouches[0].clientX); };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) nextAyah();
    if (distance < -50) prevAyah();
  };

  useEffect(() => {
    if (surahList.length === 0) {
      setLoading(true);
      fetch('https://api.alquran.cloud/v1/surah').then(res => res.json()).then(data => {
        const trSurahList = data.data.map(surah => ({ ...surah, name: SURAH_NAMES_TR[surah.number - 1] || surah.englishName }));
        setSurahList(trSurahList);
        setFilteredSurahList(trSurahList);
        setLoading(false);
      }).catch(err => { console.error(err); setLoading(false); showNotification("Sure listesi alınamadı."); });
    }
  }, [surahList.length, showNotification]);

  useEffect(() => {
    if (searchQuery.trim() === '') setFilteredSurahList(surahList);
    else {
      const lowerQuery = searchQuery.toLowerCase();
      setFilteredSurahList(surahList.filter(s => s.name.toLowerCase().includes(lowerQuery) || s.number.toString() === lowerQuery));
    }
  }, [searchQuery, surahList]);

  const loadSurah = async (surah) => {
    setLoading(true); setSelectedSurah(surah); setQuranMode('read'); setCurrentAyahIndex(0);
    try {
      // 1. quran-uthmani: Arapça Metin
      // 2. tr.yazir: Elmalılı Hamdi Yazır Meali
      // 3. tr.transliteration: Türkçe Okunuş (Diyanet usulü/Türkçe karakterlerle)
      const res = await fetch(`https://api.alquran.cloud/v1/surah/${surah.number}/editions/quran-uthmani,tr.yazir,tr.transliteration`);
      const data = await res.json();
      
      const combined = data.data[0].ayahs.map((ayah, index) => {
        const textTr = data.data[1] ? data.data[1].ayahs[index].text : "Meal yüklenemedi";
        const textLat = data.data[2] ? data.data[2].ayahs[index].text : "Okunuş yüklenemedi";

        return {
          type: 'quran-ayah',
          id: `quran-${surah.number}-${ayah.numberInSurah}`,
          surahName: surah.name,
          surahNumber: surah.number,
          number: ayah.numberInSurah,
          textAr: ayah.text,
          textTr: textTr,
          textLat: textLat, // Bu artık Türkçe karakterlerle okunuş
          juz: ayah.juz,
          page: ayah.page
        };
      });
      setQuranData(combined); setLoading(false);
    } catch (error) { console.error(error); setLoading(false); showNotification("İçerik yüklenemedi."); setQuranMode('list'); }
  };

  const nextAyah = () => { if (currentAyahIndex < quranData.length - 1) { setSlideDirection('right'); setCurrentAyahIndex(prev => prev + 1); } else showNotification("Surenin sonu."); };
  const prevAyah = () => { if (currentAyahIndex > 0) { setSlideDirection('left'); setCurrentAyahIndex(prev => prev - 1); } };

  return (
    <div className="fade-in min-h-70">
      {quranMode === 'list' && (
        <>
          <div className="sticky-search">
            <div className="search-box">
              <Search className="search-icon" />
              <input type="text" placeholder="Sure ara (örn: Yasin)" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
            </div>
          </div>
          {loading ? (<div className="loading-state"><Loader2 className="icon-lg spin text-emerald" /><p>Sureler Yükleniyor...</p></div>) : (
            <div className="list-container">
              {filteredSurahList.map((surah) => (
                <div key={surah.number} onClick={() => loadSurah(surah)} className="list-card">
                  <div className="list-card-left"><div className="number-badge">{surah.number}</div><div><h3 className="list-title">{surah.name}</h3><p className="list-subtitle">{surah.englishName}</p></div></div><ChevronRight className="icon-sm text-gray" />
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {quranMode === 'read' && selectedSurah && (
        <div className="read-view">
          <div className="read-header">
            <div className="header-left-group">
              <button onClick={() => setQuranMode('list')} className="btn-icon border"><ArrowLeft className="icon-sm" /></button>
              <div className="header-nav-arrows">
                  <button onClick={prevAyah} disabled={currentAyahIndex === 0} className={`btn-nav-arrow ${currentAyahIndex === 0 ? 'opacity-30' : ''}`}><ChevronLeft className="icon-sm" /></button>
                  <button onClick={nextAyah} disabled={currentAyahIndex === quranData.length - 1} className={`btn-nav-arrow ${currentAyahIndex === quranData.length - 1 ? 'opacity-30' : ''}`}><ChevronRight className="icon-sm" /></button>
              </div>
            </div>
            <div className="header-center"><h3 className="header-title">{selectedSurah.name}</h3><p className="header-subtitle">{selectedSurah.englishName}</p></div>
            {quranData.length > 0 && <button onClick={() => toggleFavorite(quranData[currentAyahIndex])} className="btn-icon"><Heart className={`icon-sm ${favorites.some(f => f.id === quranData[currentAyahIndex].id) ? 'text-red fill-red' : 'text-gray'}`} /></button>}
          </div>

          {loading ? (<div className="loading-state"><Loader2 className="icon-lg spin text-emerald" /><p>Ayetler getiriliyor...</p></div>) : quranData.length > 0 ? (
            <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} key={currentAyahIndex} className={`card read-card ${slideDirection === 'right' ? 'slide-in-right' : 'slide-in-left'}`}> 
              <div className="watermark">{currentAyahIndex + 1}</div>
              <div className="read-content">
                <div className="meal-section"><p className="meal-text">{quranData[currentAyahIndex].textTr}</p><p className="translator-text">Elmalılı Hamdi Yazır</p></div>
                <div className="latin-box"><p className="latin-text">"{quranData[currentAyahIndex].textLat}"</p></div>
                <p className="arabic-text" dir="rtl">{quranData[currentAyahIndex].textAr}</p>
              </div>
              <div className="read-footer">
                  <div className="page-info">Ayet {currentAyahIndex + 1} / {quranData.length}</div>
                  <div className="goto-box"><span>Git:</span><select value={currentAyahIndex} onChange={(e) => setCurrentAyahIndex(Number(e.target.value))} className="page-select">{quranData.map((_, idx) => (<option key={idx} value={idx}>{idx + 1}</option>))}</select></div>
              </div>
              <div className="swipe-hint-bottom"><ChevronLeft className="icon-xs" /> <span>Diğer ayet için kaydırın</span> <ChevronRight className="icon-xs" /></div>
            </div>
          ) : <div className="error-state">Hata.</div>}
        </div>
      )}
    </div>
  );
};

const HadithView = ({ favorites, toggleFavorite, showNotification }) => {
  const [hadithMode, setHadithMode] = useState('library'); 
  const [selectedBook, setSelectedBook] = useState(null);
  const [hadithSearch, setHadithSearch] = useState('');
  const [filteredHadiths, setFilteredHadiths] = useState([]);
  const [hadithPage, setHadithPage] = useState(1);
  const hadithsPerPage = 5;

  useEffect(() => {
    let sourceList = fullHadithCollection;
    if (hadithMode === 'book' && selectedBook) sourceList = fullHadithCollection.filter(h => h.bookId === selectedBook.id);
    if (hadithSearch.trim() === '') setFilteredHadiths(sourceList);
    else {
      const lower = hadithSearch.toLowerCase();
      setFilteredHadiths(sourceList.filter(h => h.text.toLowerCase().includes(lower) || h.topic.toLowerCase().includes(lower) || h.source.toLowerCase().includes(lower)));
    }
    setHadithPage(1); 
  }, [hadithSearch, hadithMode, selectedBook]);

  const openBook = (book) => { setSelectedBook(book); setHadithMode('book'); setHadithSearch(''); };
  const currentHadiths = filteredHadiths.slice((hadithPage - 1) * hadithsPerPage, hadithPage * hadithsPerPage);
  const totalPages = Math.ceil(filteredHadiths.length / hadithsPerPage);

  return (
    <div className="fade-in min-h-70">
       {hadithMode === 'library' && (
         <>
           <div className="section-header">
             <h2 className="section-title"><Library className="icon-md text-emerald" /> Hadis Kütüphanesi</h2>
             <p className="section-subtitle">Okumak istediğiniz kitabı seçiniz.</p>
           </div>
           <div className="library-grid">
             {hadithBooks.map(book => (
               <div key={book.id} onClick={() => openBook(book)} className={`book-card ${book.cssClass}`}>
                  <div className="book-icon"><Book className="icon-xl" /></div>
                  <div className="book-info"><h3 className="book-title">{book.name}</h3><p className="book-author">{book.author}</p></div>
                  <span className="book-count">{book.count}</span>
               </div>
             ))}
           </div>
         </>
       )}
       {hadithMode === 'book' && selectedBook && (
         <div className="read-view">
            <div className="sticky-search">
              <div className="search-header-row">
                 <button onClick={() => {setHadithMode('library'); setHadithSearch('');}} className="btn-icon border"><ArrowLeft className="icon-sm" /></button>
                 <div><h2 className="header-title">{selectedBook.name}</h2><p className="header-subtitle">Hadisler Listeleniyor</p></div>
              </div>
              <div className="search-box">
                <Search className="search-icon" />
                <input type="text" placeholder="Kitap içinde ara..." value={hadithSearch} onChange={(e) => setHadithSearch(e.target.value)} className="search-input" />
              </div>
            </div>

            <div className="hadith-list">
              {currentHadiths.length > 0 ? currentHadiths.map((h) => (
                <div key={h.id} className="card hadith-card fade-in">
                  <div className="hadith-card-header">
                     <span className="topic-badge">{h.topic}</span>
                     <div className="hadith-actions">
                        <button onClick={() => toggleFavorite(h)} className="btn-icon-sm"><Heart className={`icon-xs ${favorites.some(f => f.id === h.id) ? 'text-red fill-red' : 'text-gray'}`} /></button>
                        <button onClick={() => {navigator.clipboard.writeText(`"${h.text}" - ${h.source}`); showNotification("Kopyalandı");}} className="btn-icon-sm"><Share2 className="icon-xs text-gray" /></button>
                     </div>
                  </div>
                  <p className="hadith-body">"{h.text}"</p>
                  <p className="hadith-source">{h.source}</p>
                </div>
              )) : (
                <div className="empty-state"><BookOpen className="icon-lg opacity-20" /><p>Sonuç bulunamadı.</p></div>
              )}
            </div>
            {filteredHadiths.length > hadithsPerPage && (
              <div className="pagination">
                 <button onClick={() => setHadithPage(prev => Math.max(prev - 1, 1))} disabled={hadithPage === 1} className="btn-page"><ChevronLeft className="icon-sm" /> Önceki</button>
                 <span className="page-count">Sayfa {hadithPage} / {totalPages}</span>
                 <button onClick={() => setHadithPage(prev => Math.min(prev + 1, totalPages))} disabled={hadithPage === totalPages} className="btn-page">Sonraki <ChevronRight className="icon-sm" /></button>
              </div>
            )}
         </div>
       )}
    </div>
  );
};

const PrayersView = ({ prayerTimes, notificationsEnabled, toggleNotifications }) => {
  const [info, setInfo] = useState({ nextPrayer: null, timeLeft: '' });
  
  useEffect(() => {
    if (prayerTimes) {
      const timer = setInterval(() => setInfo(calculateNextPrayerInfo(prayerTimes)), 1000);
      return () => clearInterval(timer);
    }
  }, [prayerTimes]);

  const PrayerTimeRow = ({ name, time, isNext }) => (
    <div className={`list-item ${isNext ? 'list-item-active' : ''}`}>
      <span className={`list-item-title ${isNext ? 'text-emerald' : 'text-gray'}`}>{name}</span>
      <div className="list-item-right">
        {isNext && <span className="badge badge-emerald-light pulse">Yaklaşıyor</span>}
        <span className={`list-item-time ${isNext ? 'text-emerald' : 'text-dark'}`}>{time}</span>
      </div>
    </div>
  );

  return (
    <div className="fade-in">
       <div className="prayers-header-card">
          <div className="prayers-info">
            <p className="next-prayer-label">{info.nextPrayer?.name || 'Bekleniyor...'} Vaktine Kalan</p>
            <div className="timer-text">{info.timeLeft || '--:--:--'}</div>
            <button onClick={toggleNotifications} className={`btn-notify ${notificationsEnabled ? 'active' : ''}`}>
              {notificationsEnabled ? <Bell className="icon-xs fill-current" /> : <BellOff className="icon-xs" />}
              {notificationsEnabled ? 'Bildirimler Açık' : 'Bildirimler Kapalı'}
            </button>
          </div>
        </div>
        {prayerTimes && <div className="prayers-list">
          <PrayerTimeRow name="İmsak" time={prayerTimes.Fajr} isNext={info.nextPrayer?.name === 'İmsak'} />
          <PrayerTimeRow name="Güneş" time={prayerTimes.Sunrise} isNext={info.nextPrayer?.name === 'Güneş'} />
          <PrayerTimeRow name="Öğle" time={prayerTimes.Dhuhr} isNext={info.nextPrayer?.name === 'Öğle'} />
          <PrayerTimeRow name="İkindi" time={prayerTimes.Asr} isNext={info.nextPrayer?.name === 'İkindi'} />
          <PrayerTimeRow name="Akşam" time={prayerTimes.Maghrib} isNext={info.nextPrayer?.name === 'Akşam'} />
          <PrayerTimeRow name="Yatsı" time={prayerTimes.Isha} isNext={info.nextPrayer?.name === 'Yatsı'} />
        </div>}
    </div>
  );
};

const QiblaView = ({ qiblaAngle, showNotification }) => {
  const [deviceHeading, setDeviceHeading] = useState(0);
  const [isSensorDataReceived, setIsSensorDataReceived] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { if (!isSensorDataReceived) setManualMode(true); }, 3000);
    return () => clearTimeout(timer);
  }, [isSensorDataReceived]);

  const handleOrientation = (event) => {
    let heading = null;
    if (event.webkitCompassHeading) heading = event.webkitCompassHeading;
    else if (event.alpha !== null) heading = 360 - event.alpha; 
    if (heading !== null) { setIsSensorDataReceived(true); setManualMode(false); setDeviceHeading(heading); }
  };

  const startCompass = () => {
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission().then(response => {
        if (response === 'granted') { setIsSensorDataReceived(true); setManualMode(false); window.addEventListener('deviceorientation', handleOrientation); } 
        else { showNotification("İzin verilmedi."); setManualMode(true); }
      }).catch(() => setManualMode(true));
    } else {
      setIsSensorDataReceived(true); setManualMode(false);
      window.addEventListener('deviceorientationabsolute', handleOrientation, true);
      window.addEventListener('deviceorientation', handleOrientation, true);
    }
  };

  return (
    <div className="fade-in flex-col-center">
      <div className="compass-container">
        <div className="compass-dial" style={{ transform: `rotate(${-deviceHeading}deg)` }}>
          <span className="dir-n">K</span><span className="dir-s">G</span><span className="dir-e">D</span><span className="dir-w">B</span>
          <div className="dial-lines"></div>
          <div className="kaaba-pointer" style={{ transform: `rotate(${qiblaAngle}deg)` }}>
             <div className="kaaba-icon"><div className="kaaba-stripe"></div></div>
             <div className="arrow-down"></div>
          </div>
        </div>
      </div>
      {manualMode ? (
        <div className="manual-control">
          <input type="range" min="0" max="360" value={deviceHeading} onChange={(e) => setDeviceHeading(parseInt(e.target.value))} className="range-input" />
          <p className="info-text">Manuel Döndürme Modu</p>
        </div>
      ) : <button onClick={startCompass} className="btn-primary"><RotateCw className="icon-sm"/> Pusulayı Başlat</button>}
    </div>
  );
};

/* =========================================
   4. ANA UYGULAMA BİLEŞENİ (APP)
   ========================================= */

export default function HuzurVaktiPage() {
  const [activeTab, setActiveTab] = useState('daily');
  const [favorites, setFavorites] = useState([]);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [qiblaAngle, setQiblaAngle] = useState(0);

  // --- Genel Fonksiyonlar ---
  const showNotification = useCallback((msg) => {
    setToastMessage(msg); setShowToast(true); setTimeout(() => setShowToast(false), 3000);
  }, []);

  const toggleFavorite = (item) => {
    let newFavorites;
    if (favorites.some(fav => fav.id === item.id)) {
      newFavorites = favorites.filter(fav => fav.id !== item.id);
      showNotification("Favorilerden çıkarıldı");
    } else {
      newFavorites = [...favorites, item];
      showNotification("Favorilere eklendi");
    }
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };

  const toggleNotifications = () => {
    const newState = !notificationsEnabled;
    setNotificationsEnabled(newState);
    localStorage.setItem('notificationsEnabled', JSON.stringify(newState));
    showNotification(newState ? "Bildirimler açıldı" : "Bildirimler kapatıldı");
  };

  // --- Başlangıç Verileri ---
  useEffect(() => {
    const savedFavs = localStorage.getItem('favorites');
    if (savedFavs) setFavorites(JSON.parse(savedFavs));
    
    const savedNotif = localStorage.getItem('notificationsEnabled');
    if (savedNotif) setNotificationsEnabled(JSON.parse(savedNotif));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setQiblaAngle(calculateQiblaAngle(lat, lng));
          try {
            const date = new Date();
            const dateString = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
            const res = await fetch(`https://api.aladhan.com/v1/timings/${dateString}?latitude=${lat}&longitude=${lng}&method=13`); 
            const data = await res.json();
            if (data.code === 200) setPrayerTimes(data.data.timings);
          } catch (e) { console.error(e); }
        },
        (error) => { /* Hata yönetimi (Varsayılan İstanbul vs.) eklenebilir */ }
      );
    }
  }, []);

  return (
    <div className="app-root">
      <Header onOpenFavorites={() => setShowFavoritesModal(true)} hasFavorites={favorites.length > 0} />

      <main className="main-content">
        {activeTab === 'daily' && <DailyView favorites={favorites} toggleFavorite={toggleFavorite} showNotification={showNotification} prayerTimes={prayerTimes} />}
        {activeTab === 'quran' && <QuranView favorites={favorites} toggleFavorite={toggleFavorite} showNotification={showNotification} />}
        {activeTab === 'hadith' && <HadithView favorites={favorites} toggleFavorite={toggleFavorite} showNotification={showNotification} />}
        {activeTab === 'prayers' && <PrayersView prayerTimes={prayerTimes} notificationsEnabled={notificationsEnabled} toggleNotifications={toggleNotifications} />}
        {activeTab === 'qibla' && <QiblaView qiblaAngle={qiblaAngle} showNotification={showNotification} />}
      </main>

      <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      <FavoritesModal show={showFavoritesModal} onClose={() => setShowFavoritesModal(false)} favorites={favorites} toggleFavorite={toggleFavorite} />
      
      {showToast && <div className="toast-message">{toastMessage}</div>}

      <style>{`
        :root { --primary: #059669; --primary-dark: #047857; --primary-light: #d1fae5; --secondary: #0f172a; --bg-app: #f8fafc; --surface: #ffffff; --text-main: #1e293b; --text-muted: #64748b; --border: #e2e8f0; --red: #ef4444; --shadow: 0 4px 6px -1px rgba(0,0,0,0.1); --shadow-lg: 0 10px 15px -3px rgba(0,0,0,0.1); --safe-bottom: env(safe-area-inset-bottom); }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background-color: var(--bg-app); color: var(--text-main); }
        button { border: none; background: none; cursor: pointer; font-family: inherit; padding: 0; }
        .app-root { min-height: 100vh; padding-bottom: calc(4rem + var(--safe-bottom)); display: flex; flex-direction: column; overflow-x: hidden; width: 100%; }
        /* ICONS & UTILS */
        .icon-xs { width: 14px; height: 14px; } .icon-sm { width: 20px; height: 20px; } .icon-md { width: 24px; height: 24px; } .icon-lg { width: 32px; height: 32px; } .icon-xl { width: 48px; height: 48px; }
        .text-white { color: white; } .text-gray { color: #94a3b8; } .text-emerald { color: var(--primary); } .text-red { color: var(--red); } .fill-current { fill: currentColor; } .fill-red { fill: var(--red); } .fill-emerald-light { fill: var(--primary-light); }
        .spin { animation: spin 1s linear infinite; }
        /* HEADER */
        .header { position: sticky; top: 0; z-index: 50; background: rgba(255,255,255,0.9); backdrop-filter: blur(10px); border-bottom: 1px solid var(--border); height: 3.5rem; padding: 0 1rem; display: flex; justify-content: space-between; align-items: center; }
        .header-left { display: flex; align-items: center; gap: 0.5rem; }
        .logo-box { width: 2rem; height: 2rem; background: linear-gradient(to top right, #059669, #14b8a6); border-radius: 0.5rem; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-family: serif; }
        .app-title { font-size: 1rem; font-weight: 700; margin: 0; line-height: 1; }
        .app-subtitle { font-size: 0.65rem; color: var(--text-muted); margin: 0; font-weight: 500; }
        /* MAIN */
        .main-content { max-width: 28rem; margin: 0 auto; padding: 1rem; width: 100%; flex: 1; overflow-x: hidden; }
        .fade-in { animation: fadeIn 0.3s ease-out; }
        /* CARDS */
        .card { background: var(--surface); border-radius: 1.5rem; overflow: hidden; box-shadow: var(--shadow-lg); position: relative; margin-bottom: 1rem; }
        .daily-card { height: 380px; display: flex; flex-direction: column; }
        .card-header-bg { height: 6rem; display: flex; flex-direction: column; align-items: center; justify-content: center; position: relative; padding: 0.75rem; text-align: center; }
        .pattern-overlay { position: absolute; inset: 0; opacity: 0.1; background-image: url("https://www.transparenttextures.com/patterns/arabesque.png"); }
        .card-emerald { background: linear-gradient(to right, #10b981, #0f766e); } .card-indigo { background: linear-gradient(to right, #6366f1, #1d4ed8); } .card-amber { background: linear-gradient(to right, #f59e0b, #c2410c); } .card-rose { background: linear-gradient(to right, #f43f5e, #be123c); }
        .card-body { padding: 1rem; display: flex; flex-direction: column; gap: 1rem; flex: 1; overflow-y: auto; }
        .ayah-text { font-family: serif; font-size: 1.125rem; font-style: italic; line-height: 1.4; margin: 0 0 0.5rem 0; text-align: center; }
        .source-text { font-size: 0.75rem; color: var(--text-muted); font-weight: 500; border-top: 1px solid var(--border); display: inline-block; padding-top: 0.25rem; margin: 0; text-align: center; width: 100%; }
        .action-row { display: flex; justify-content: center; gap: 0.75rem; margin-top: auto; padding-top: 0.5rem; }
        .btn-circle { padding: 0.6rem; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
        .btn-red { background: #fef2f2; color: var(--red); } .btn-gray { background: #f8fafc; color: var(--text-muted); }
        /* HOME PRAYER */
        .home-prayer-container { margin-top: 1.5rem; background: white; padding: 1rem; border-radius: 1rem; box-shadow: var(--shadow); border: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
        .timer-box { font-family: monospace; font-size: 1.5rem; font-weight: 700; background: var(--bg-app); padding: 0.25rem 0.75rem; border-radius: 0.5rem; }
        .dots-container { display: flex; justify-content: center; gap: 0.35rem; margin-top: 0.5rem; }
        .dot { height: 0.35rem; width: 0.35rem; border-radius: 99px; background: #cbd5e1; transition: width 0.3s; }
        .dot-active { width: 1rem; background: var(--primary); }
        /* NAV */
        .bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; background: white; border-top: 1px solid var(--border); z-index: 40; padding-bottom: calc(var(--safe-bottom) + 0.5rem); box-shadow: 0 -4px 6px -1px rgba(0,0,0,0.05); }
        .nav-grid { display: grid; grid-template-columns: repeat(5, 1fr); max-width: 28rem; margin: 0 auto; position: relative; padding-top: 0.5rem; }
        .nav-item { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; color: #94a3b8; font-size: 0.6rem; font-weight: 500; padding-bottom: 0.5rem; }
        .nav-item.active { color: var(--primary); }
        .fab-wrapper { position: relative; display: flex; justify-content: center; }
        .fab-btn { position: absolute; top: -2rem; width: 3.5rem; height: 3.5rem; border-radius: 50%; background: var(--primary); border: 4px solid var(--surface); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); display: flex; align-items: center; justify-content: center; }
        /* QURAN & HADITH LISTS */
        .sticky-search { position: sticky; top: 0; background: var(--bg-app); z-index: 20; padding-bottom: 0.5rem; }
        .search-box { position: relative; }
        .search-icon { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); width: 1rem; height: 1rem; color: #94a3b8; }
        .search-input { width: 100%; background: white; border: 1px solid var(--border); border-radius: 0.75rem; padding: 0.75rem 1rem 0.75rem 2.5rem; font-size: 0.875rem; outline: none; }
        .search-input:focus { border-color: var(--primary); }
        .list-container { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 0.5rem; }
        .list-card { background: white; padding: 1rem; border-radius: 0.75rem; border: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; cursor: pointer; }
        .list-card-left { display: flex; align-items: center; gap: 0.75rem; }
        .number-badge { width: 2rem; height: 2rem; background: var(--primary-light); color: var(--primary-dark); border-radius: 0.5rem; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.875rem; }
        .list-title { font-weight: 700; font-size: 1rem; margin: 0; }
        .list-subtitle { font-size: 0.75rem; color: var(--text-muted); margin: 0; }
        /* READ VIEW */
        .read-view { display: flex; flex-direction: column; height: 100%; width: 100%; }
        .read-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
        .header-left-group { display: flex; align-items: center; gap: 0.75rem; }
        .header-nav-arrows { display: flex; gap: 0.5rem; }
        .read-card { padding: 1.5rem; height: 60vh; display: flex; flex-direction: column; width: 100%; }
        .btn-nav-arrow { background-color: #d1fae5; color: #047857; padding: 0.35rem; border-radius: 0.5rem; display: flex; align-items: center; justify-content: center; }
        .watermark { position: absolute; top: 0; right: 0; padding: 1rem; font-size: 4rem; font-family: serif; color: var(--primary); opacity: 0.1; pointer-events: none; }
        .read-content { overflow-y: auto; flex: 1; padding-bottom: 2rem; }
        .meal-section { border-bottom: 1px solid var(--border); padding-bottom: 1rem; margin-bottom: 1rem; }
        .meal-text { font-size: 1rem; font-weight: 500; line-height: 1.6; }
        .translator-text { font-size: 0.65rem; color: var(--text-muted); text-align: right; margin-top: 0.5rem; font-style: italic; }
        .arabic-text { font-family: serif; font-size: 1.75rem; text-align: right; line-height: 1.8; margin: 0; }
        .read-footer { padding-top: 1rem; border-top: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; margin-top: auto; }
        .swipe-hint-bottom { display: flex; justify-content: center; align-items: center; gap: 0.25rem; margin-top: 0.5rem; font-size: 0.65rem; color: #94a3b8; opacity: 0.5; animation: pulse 3s infinite; }
        /* LIBRARY */
        .library-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .book-card { padding: 1rem; border-radius: 1rem; color: white; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; height: 8rem; position: relative; overflow: hidden; }
        .book-blue { background: #2563eb; } .book-emerald { background: #059669; } .book-amber { background: #d97706; } .book-rose { background: #e11d48; } .book-slate { background: #475569; } .book-indigo { background: #4f46e5; }
        .book-icon { position: absolute; right: -0.5rem; bottom: -0.5rem; opacity: 0.2; transform: rotate(12deg); }
        .hadith-list { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 0.5rem; min-height: 40vh; }
        .hadith-card { padding: 1rem; border: 1px solid var(--border); }
        .topic-badge { background: #f1f5f9; color: #475569; font-size: 0.65rem; padding: 0.15rem 0.5rem; border-radius: 0.25rem; font-weight: 500; }
        .hadith-body { font-size: 0.875rem; line-height: 1.5; margin: 0 0 0.5rem 0; font-family: serif; }
        .pagination { display: flex; justify-content: space-between; align-items: center; padding: 1.5rem 0; border-top: 1px solid var(--border); }
        .btn-page { display: flex; align-items: center; gap: 0.25rem; background: white; border: 1px solid var(--border); padding: 0.35rem 0.75rem; border-radius: 0.5rem; font-size: 0.875rem; }
        /* PRAYERS */
        .prayers-header-card { background: linear-gradient(135deg, #1e293b, #0f172a); border-radius: 1rem; padding: 1rem; color: white; margin-bottom: 1rem; box-shadow: var(--shadow-lg); }
        .prayers-info { text-align: center; }
        .timer-text { font-size: 2rem; font-weight: 700; font-family: monospace; margin-bottom: 0.5rem; }
        .btn-notify { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.35rem 0.75rem; border-radius: 99px; font-size: 0.75rem; background: rgba(255,255,255,0.1); color: #94a3b8; }
        .btn-notify.active { background: rgba(16, 185, 129, 0.2); color: #d1fae5; border: 1px solid rgba(16, 185, 129, 0.5); }
        .list-item { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; border-radius: 0.75rem; background: white; border: 1px solid var(--border); margin-bottom: 0.25rem; }
        .list-item-active { background: var(--primary-light); border-left: 4px solid var(--primary); }
        .badge-emerald-light { background: #a7f3d0; color: #065f46; font-size: 0.65rem; padding: 0.15rem 0.4rem; border-radius: 99px; }
        /* COMPASS */
        .flex-col-center { display: flex; flex-direction: column; align-items: center; }
        .compass-container { position: relative; width: 16rem; height: 16rem; margin: 2rem 0 1.5rem 0; }
        .compass-dial { position: absolute; inset: 0; border-radius: 50%; border: 4px solid var(--border); background: white; box-shadow: var(--shadow-lg); display: flex; align-items: center; justify-content: center; transition: transform 0.3s ease-out; }
        .dir-n { position: absolute; top: 0.5rem; color: var(--red); font-weight: 700; font-size: 1.1rem; }
        .dir-s, .dir-e, .dir-w { position: absolute; font-weight: 700; font-size: 1.1rem; color: #94a3b8; }
        .dir-s { bottom: 0.5rem; } .dir-e { right: 0.5rem; } .dir-w { left: 0.5rem; }
        .kaaba-pointer { position: absolute; width: 100%; height: 100%; display: flex; justify-content: center; }
        .kaaba-icon { position: absolute; top: 2rem; width: 1.5rem; height: 1.5rem; background: black; border-radius: 2px; display: flex; align-items: center; justify-content: center; border: 1px solid #eab308; }
        .kaaba-stripe { width: 100%; height: 30%; border-bottom: 2px solid #eab308; opacity: 0.5; }
        .arrow-down { position: absolute; top: 3.6rem; width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-bottom: 12px solid var(--primary); }
        .btn-primary { background: var(--primary); color: white; padding: 0.5rem 1.25rem; border-radius: 99px; font-size: 0.75rem; font-weight: 500; display: flex; align-items: center; gap: 0.5rem; box-shadow: var(--shadow); }
        /* MODAL */
        .modal-overlay { position: fixed; inset: 0; z-index: 50; background: rgba(0,0,0,0.5); backdrop-filter: blur(2px); display: flex; align-items: flex-end; justify-content: center; padding: 1rem; }
        .modal-content { background: white; width: 100%; max-width: 28rem; border-radius: 1rem 1rem 0 0; height: 80vh; display: flex; flex-direction: column; box-shadow: 0 -10px 25px rgba(0,0,0,0.1); }
        .modal-header { padding: 1rem; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
        .modal-body { padding: 1rem; overflow-y: auto; flex-grow: 1; }
        .fav-card { padding: 0.75rem; border: 1px solid var(--border); margin-bottom: 0.75rem; }
        .badge { font-size: 0.65rem; padding: 0.15rem 0.4rem; border-radius: 99px; }
        .toast-message { position: fixed; top: 5rem; left: 50%; transform: translateX(-50%); background: rgba(30, 41, 59, 0.9); backdrop-filter: blur(4px); color: white; padding: 0.5rem 1rem; border-radius: 99px; font-size: 0.75rem; font-weight: 500; z-index: 60; animation: bounce 0.5s; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bounce { 0%, 100% { transform: translate(-50%, 0); } 50% { transform: translate(-50%, -5px); } }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideInLeft { from { transform: translateX(-100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @media (min-width: 640px) { .modal-content { border-radius: 1rem; height: auto; max-height: 80vh; align-self: center; } }
      `}</style>
    </div>
  );
}